import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Download,
  ChevronDown,
  ChevronRight,
  Zap,
  ShieldCheck,
  Clock,
  Info,
  Calendar
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const API_URL = 'https://api.glowytics.com';

const PLATFORM_META = {
  GOOGLE_ADS:       { label: 'Google Ads',        logo: '/google_ads-512.webp',             bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-700'   },
  GOOGLE_ANALYTICS: { label: 'Google Analytics',   logo: '/GA4logoicon.svg',                 bg: 'bg-yellow-50',  border: 'border-yellow-200',  text: 'text-yellow-700' },
  META_ADS:         { label: 'Meta Ads',           logo: '/6033716.png',                     bg: 'bg-indigo-50',  border: 'border-indigo-200',  text: 'text-indigo-700' },
  SEARCH_CONSOLE:   { label: 'Search Console',     logo: '/search-console-icon-2025-1v33.svg', bg: 'bg-sky-50',  border: 'border-sky-200',     text: 'text-sky-700'    },
  IKAS:             { label: 'ikas',               logo: '/ikaslogoicon.png',                bg: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-700', rounded: true },
  SHOPIFY:          { label: 'Shopify',            logo: '/shopifyicon.png',                 bg: 'bg-green-50',   border: 'border-green-200',   text: 'text-green-700'  },
  WOOCOMMERCE:      { label: 'WooCommerce',        logo: '/woocommerceicon.png',             bg: 'bg-purple-50',  border: 'border-purple-200',  text: 'text-purple-700' },
  ADJUST:           { label: 'Adjust',             logo: '/adjustlogopng.png',               bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-700'    },
};

const GAP_TYPE_LABELS = {
  NO_SYNC:      { label: 'Not synced',           color: 'bg-orange-100 text-orange-700', icon: <Clock className="w-3.5 h-3.5" /> },
  SYNC_ERROR:   { label: 'Sync error',           color: 'bg-red-100 text-red-700',       icon: <XCircle className="w-3.5 h-3.5" /> },
  SYNC_STUCK:   { label: 'Sync stuck',           color: 'bg-yellow-100 text-yellow-700', icon: <AlertTriangle className="w-3.5 h-3.5" /> },
  MISSING_DATA: { label: 'Data not found',       color: 'bg-gray-100 text-gray-600',     icon: <AlertTriangle className="w-3.5 h-3.5" /> },
  NO_TABLE:     { label: 'Table not created',    color: 'bg-gray-100 text-gray-500',     icon: <Info className="w-3.5 h-3.5" /> },
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Group gaps by year-month
function groupGapsByMonth(gaps) {
  const groups = {};
  for (const gap of gaps) {
    const [year, month] = gap.date.split('-');
    const key = `${year}-${month}`;
    if (!groups[key]) {
      groups[key] = {
        key,
        year: Number(year),
        month: Number(month),
        label: `${MONTH_NAMES[Number(month) - 1]} ${year}`,
        gaps: []
      };
    }
    groups[key].gaps.push(gap);
  }
  // Sort newest month first
  return Object.values(groups).sort((a, b) => b.key.localeCompare(a.key));
}

export default function DataHealth({ embedded = false }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});          // platform-level
  const [expandedMonths, setExpandedMonths] = useState({}); // platform:month-level
  const [backfilling, setBackfilling] = useState({});
  const [backfillingAll, setBackfillingAll] = useState(false);

  const fetchGaps = useCallback(async () => {
    try {
      setLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) { setLoading(false); return; }

      const res = await fetch(`${API_URL}/api/data-sync-logs/gaps`, {
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        credentials: 'include'
      });
      if (!res.ok) throw new Error('API error');
      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error('Gap detection error:', err);
      toast.error('Data health scan failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGaps(); }, [fetchGaps]);

  useEffect(() => {
    document.title = 'Data Health | Glowytics';
  }, []);

  const handleBackfillPlatform = async (platform) => {
    const p = data?.platforms?.find(pp => pp.platform === platform);
    if (!p || p.gaps.length === 0) return;

    setBackfilling(prev => ({ ...prev, [platform]: true }));
    const toastId = toast.loading(`Backfilling ${PLATFORM_META[platform]?.label || platform} data...`);

    try {
      const brandId = localStorage.getItem('currentBrandId');
      const res = await fetch(`${API_URL}/api/data-sync-logs/gaps/backfill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        credentials: 'include',
        body: JSON.stringify({
          items: [{ platform, dates: p.gaps.map(g => g.date) }]
        })
      });
      const json = await res.json();
      if (json.success) {
        const triggered = json.results?.filter(r => r.status === 'TRIGGERED').length || 0;
        toast.success(`Data fetch started for ${triggered} date range(s)`, { id: toastId });
        setTimeout(() => fetchGaps(), 5000);
      } else {
        toast.error('Backfill failed', { id: toastId });
      }
    } catch (err) {
      toast.error('Backfill error: ' + err.message, { id: toastId });
    } finally {
      setBackfilling(prev => ({ ...prev, [platform]: false }));
    }
  };

  const handleBackfillMonth = async (platform, monthGaps) => {
    if (!monthGaps || monthGaps.length === 0) return;

    const key = `${platform}_month`;
    setBackfilling(prev => ({ ...prev, [key]: true }));
    const toastId = toast.loading(`Backfilling ${PLATFORM_META[platform]?.label || platform} monthly data...`);

    try {
      const brandId = localStorage.getItem('currentBrandId');
      const res = await fetch(`${API_URL}/api/data-sync-logs/gaps/backfill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        credentials: 'include',
        body: JSON.stringify({
          items: [{ platform, dates: monthGaps.map(g => g.date) }]
        })
      });
      const json = await res.json();
      if (json.success) {
        const triggered = json.results?.filter(r => r.status === 'TRIGGERED').length || 0;
        toast.success(`Data fetch started for ${triggered} date range(s)`, { id: toastId });
        setTimeout(() => fetchGaps(), 5000);
      } else {
        toast.error('Backfill failed', { id: toastId });
      }
    } catch (err) {
      toast.error('Backfill error: ' + err.message, { id: toastId });
    } finally {
      setBackfilling(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleBackfillAll = async () => {
    if (!data?.platforms) return;
    const platformsWithGaps = data.platforms.filter(p => p.gaps.length > 0 && p.status !== 'NO_TABLE');
    if (platformsWithGaps.length === 0) return;

    setBackfillingAll(true);
    const toastId = toast.loading('Backfilling all missing data...');

    try {
      const brandId = localStorage.getItem('currentBrandId');
      const items = platformsWithGaps.map(p => ({
        platform: p.platform,
        dates: p.gaps.map(g => g.date)
      }));

      const res = await fetch(`${API_URL}/api/data-sync-logs/gaps/backfill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        credentials: 'include',
        body: JSON.stringify({ items })
      });
      const json = await res.json();
      if (json.success) {
        const triggered = json.results?.filter(r => r.status === 'TRIGGERED').length || 0;
        toast.success(`${triggered} data fetch(es) started!`, { id: toastId });
        setTimeout(() => fetchGaps(), 8000);
      } else {
        toast.error('Bulk backfill failed', { id: toastId });
      }
    } catch (err) {
      toast.error('Backfill error: ' + err.message, { id: toastId });
    } finally {
      setBackfillingAll(false);
    }
  };

  const toggleExpand = (platform) => {
    setExpanded(prev => ({ ...prev, [platform]: !prev[platform] }));
  };

  const toggleMonth = (platform, monthKey) => {
    const compositeKey = `${platform}:${monthKey}`;
    setExpandedMonths(prev => ({ ...prev, [compositeKey]: !prev[compositeKey] }));
  };

  // Calculate health score
  const healthScore = data ? (() => {
    const total = data.platforms?.reduce((s, p) => s + p.totalDays, 0) || 1;
    const present = data.platforms?.reduce((s, p) => s + p.presentDays, 0) || 0;
    return Math.round((present / total) * 100);
  })() : 0;

  const healthyPlatforms = data?.platforms?.filter(p => p.status === 'HEALTHY').length || 0;
  const totalPlatforms = data?.platforms?.length || 0;
  const totalGaps = data?.totalGaps || 0;

  if (loading && !data) {
    return (
      <div className={embedded ? '' : 'p-6 max-w-7xl mx-auto'}>
        <div className="animate-pulse space-y-6">
          {!embedded && <div className="h-8 bg-gray-200 rounded w-1/3"></div>}
          <div className="h-40 bg-gray-200 rounded"></div>
          <div className="space-y-4">
            {[1,2,3].map(i => <div key={i} className="h-24 bg-gray-200 rounded"></div>)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 max-w-7xl mx-auto space-y-6'}>
      {/* Header - only shown in standalone mode */}
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShieldCheck className="w-7 h-7 text-indigo-600" />
              Data Health
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Monitor data completeness across all your platforms and backfill missing data with one click
            </p>
          </div>
          <button
            onClick={fetchGaps}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Rescan
          </button>
        </div>
      )}

      {/* Health Overview Card */}
      <div className={`rounded-xl border-2 p-6 ${
        totalGaps === 0 
          ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200' 
          : totalGaps <= 5 
            ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200'
            : 'bg-gradient-to-r from-red-50 to-orange-50 border-red-200'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            {/* Health Score */}
            <div className="relative w-20 h-20 flex-shrink-0">
              <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 36 36">
                <path
                  d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none" stroke="#e5e7eb" strokeWidth="3"
                />
                <path
                  d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke={healthScore >= 90 ? '#10b981' : healthScore >= 70 ? '#f59e0b' : '#ef4444'}
                  strokeWidth="3"
                  strokeDasharray={`${healthScore}, 100`}
                  strokeLinecap="round"
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-gray-900">
                {healthScore}%
              </span>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {totalGaps === 0
                  ? 'All your data is complete!'
                  : `${totalGaps} missing day(s) detected`}
              </h2>
              <p className="text-sm text-gray-600 mt-0.5">
                {healthyPlatforms}/{totalPlatforms} platforms healthy
                {data?.scanDate && <span className="ml-2 text-gray-400">· Last scan: {new Date(data.scanDate).toLocaleString('en-US')}</span>}
              </p>
            </div>
          </div>
          {totalGaps > 0 && (
            <button
              onClick={handleBackfillAll}
              disabled={backfillingAll}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-50"
            >
              {backfillingAll ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Zap className="w-4 h-4" />
              )}
              Backfill All
            </button>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-indigo-800">
            <p className="font-medium mb-1">What is this page for?</p>
            <p className="text-indigo-600 leading-relaxed">
              Glowytics fetches data from all your connected platforms daily at your scheduled times.
              Sometimes server issues, API limits, or connection errors can cause data gaps for certain days.
              This page scans each platform from its first data date, detects missing days, and lets you fill those gaps with one click.
            </p>
            <div className="mt-2 flex flex-wrap gap-3">
              {Object.entries(GAP_TYPE_LABELS).filter(([k]) => k !== 'NO_TABLE').map(([key, val]) => (
                <span key={key} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${val.color}`}>
                  {val.icon} {val.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Platform Cards */}
      <div className="space-y-3">
        {data?.platforms?.map((p) => {
          const meta = PLATFORM_META[p.platform] || { label: p.platform, bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700' };
          const isExpanded = expanded[p.platform];
          const isHealthy = p.status === 'HEALTHY';
          const isNoTable = p.status === 'NO_TABLE';
          const isFilling = backfilling[p.platform];
          const monthGroups = isExpanded ? groupGapsByMonth(p.gaps) : [];
          const completionPct = Math.round((p.presentDays / Math.max(p.totalDays, 1)) * 100);

          return (
            <div
              key={p.platform}
              className={`rounded-xl border transition-all ${
                isHealthy ? 'border-green-200 bg-white' : isNoTable ? 'border-gray-200 bg-gray-50' : 'border-orange-200 bg-white'
              }`}
            >
              {/* Platform Header */}
              <div
                className={`flex items-center justify-between px-5 py-4 ${!isHealthy && !isNoTable ? 'cursor-pointer' : ''} select-none`}
                onClick={() => !isHealthy && !isNoTable && toggleExpand(p.platform)}
              >
                <div className="flex items-center gap-3">
                  {meta.logo ? (
                    <img src={meta.logo} alt="" className={`w-7 h-7 object-contain ${meta.rounded ? 'rounded-lg' : ''}`} onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className={`w-7 h-7 rounded-full ${meta.bg} flex items-center justify-center`}>
                      <span className={`text-xs font-bold ${meta.text}`}>{meta.label.charAt(0)}</span>
                    </div>
                  )}
                  <div>
                    <span className="font-semibold text-gray-900 text-[15px]">{meta.label}</span>
                    {p.firstDataDate && (
                      <span className="text-xs text-gray-400 ml-2">First data: {p.firstDataDate}</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {isHealthy ? (
                    <span className="flex items-center gap-1.5 text-sm font-medium text-green-600">
                      <CheckCircle2 className="w-5 h-5" />
                      Complete
                    </span>
                  ) : isNoTable ? (
                    <span className="flex items-center gap-1.5 text-sm text-gray-400">
                      <Info className="w-4 h-4" />
                      Table not created yet
                    </span>
                  ) : (
                    <>
                      <span className="flex items-center gap-1.5 text-sm font-medium text-orange-600">
                        <AlertTriangle className="w-4 h-4" />
                        {p.missingDays} missing day(s)
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleBackfillPlatform(p.platform); }}
                        disabled={isFilling}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                      >
                        {isFilling ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        Backfill All
                      </button>
                      {isExpanded
                        ? <ChevronDown className="w-4 h-4 text-gray-400" />
                        : <ChevronRight className="w-4 h-4 text-gray-400" />
                      }
                    </>
                  )}
                </div>
              </div>

              {/* Progress Bar */}
              {!isNoTable && p.totalDays > 0 && (
                <div className="px-5 pb-3">
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-500 ${
                        isHealthy ? 'bg-green-500' : completionPct > 80 ? 'bg-yellow-400' : 'bg-orange-500'
                      }`}
                      style={{ width: `${completionPct}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-[11px] text-gray-400">{p.presentDays}/{p.totalDays} days available</span>
                    <span className="text-[11px] text-gray-400">{completionPct}%</span>
                  </div>
                </div>
              )}

              {/* Expanded: Month Groups */}
              {isExpanded && monthGroups.length > 0 && (
                <div className="px-5 pb-4 border-t border-gray-100">
                  <div className="mt-3 space-y-2">
                    {monthGroups.map((mg) => {
                      const monthKey = `${p.platform}:${mg.key}`;
                      const isMonthOpen = expandedMonths[monthKey];
                      const isMonthFilling = backfilling[`${p.platform}_month`];

                      return (
                        <div key={mg.key} className="border border-gray-200 rounded-lg overflow-hidden">
                          {/* Month Header */}
                          <div
                            className="flex items-center justify-between px-4 py-2.5 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => toggleMonth(p.platform, mg.key)}
                          >
                            <div className="flex items-center gap-2">
                              {isMonthOpen
                                ? <ChevronDown className="w-4 h-4 text-gray-400" />
                                : <ChevronRight className="w-4 h-4 text-gray-400" />
                              }
                              <Calendar className="w-4 h-4 text-gray-500" />
                              <span className="text-sm font-medium text-gray-700">{mg.label}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                mg.gaps.length > 10 ? 'bg-red-100 text-red-700' : mg.gaps.length > 3 ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {mg.gaps.length} missing day(s)
                              </span>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleBackfillMonth(p.platform, mg.gaps); }}
                              disabled={isMonthFilling}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors disabled:opacity-50 border border-indigo-200"
                            >
                              {isMonthFilling ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                              Backfill Month
                            </button>
                          </div>

                          {/* Month Gap Details */}
                          {isMonthOpen && (
                            <div className="p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 gap-1.5">
                              {mg.gaps.map((gap) => {
                                const gapMeta = GAP_TYPE_LABELS[gap.type] || GAP_TYPE_LABELS.MISSING_DATA;
                                const dateObj = new Date(gap.date + 'T00:00:00');
                                const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
                                const dayNum = gap.date.split('-')[2];
                                return (
                                  <div
                                    key={gap.date}
                                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs ${
                                      gap.type === 'SYNC_ERROR' ? 'border-red-200 bg-red-50' :
                                      gap.type === 'NO_SYNC' ? 'border-orange-200 bg-orange-50' :
                                      gap.type === 'SYNC_STUCK' ? 'border-yellow-200 bg-yellow-50' :
                                      'border-gray-200 bg-gray-50'
                                    }`}
                                    title={`${gap.date} - ${gapMeta.label}`}
                                  >
                                    {gapMeta.icon}
                                    <span className="font-medium text-gray-800">{dayNum}</span>
                                    <span className="text-gray-400">{dayName}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {data?.platforms?.length === 0 && (
        <div className="text-center py-16">
          <ShieldCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-500">No connected platforms found</h3>
          <p className="text-sm text-gray-400 mt-1">
            Connect a data source from the Settings page, then monitor data health here.
          </p>
        </div>
      )}
    </div>
  );
}
